import React, { useState, useEffect } from 'react';
import { getUser, updateUser, updateBusinessProfile, getExpenseReportTemplate } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  User,
  Building2,
  FileText,
  Save,
  Download,
  Loader2
} from 'lucide-react';

const businessTypes = [
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'llc_single', label: 'Single-Member LLC' },
  { value: 'llc_multi', label: 'Multi-Member LLC' },
  { value: 's_corp', label: 'S Corporation' },
  { value: 'c_corp', label: 'C Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'nonprofit', label: 'Nonprofit' }
];

const industries = [
  { value: 'freelance_tech', label: 'Freelance/Tech' },
  { value: 'rideshare_driver', label: 'Rideshare Driver' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'general', label: 'General Business' }
];

const usStates = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Personal Info
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('general');
  
  // Business Profile
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [ein, setEin] = useState('');
  const [duns, setDuns] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [accountingMethod, setAccountingMethod] = useState('cash');

  async function fetchUser() {
    try {
      setLoading(true);
      const res = await getUser();
      const userData = res.data;
      setUser(userData);
      
      // Set personal info
      setName(userData.name || '');
      setIndustry(userData.industry || 'general');
      
      // Set business profile
      const bp = userData.business_profile || {};
      setBusinessName(bp.business_name || '');
      setBusinessType(bp.business_type || '');
      setEin(bp.ein || '');
      setDuns(bp.duns || '');
      setAddressLine1(bp.address_line1 || '');
      setAddressLine2(bp.address_line2 || '');
      setCity(bp.city || '');
      setState(bp.state || '');
      setZipCode(bp.zip_code || '');
      setPhone(bp.phone || '');
      setWebsite(bp.website || '');
      setAccountingMethod(bp.accounting_method || 'cash');
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchUser();
  }, []);

  async function handleSavePersonal() {
    setSaving(true);
    try {
      await updateUser({ name, industry });
      toast.success('Personal info saved');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveBusiness() {
    setSaving(true);
    try {
      await updateBusinessProfile({
        business_name: businessName || null,
        business_type: businessType || null,
        ein: ein || null,
        duns: duns || null,
        address_line1: addressLine1 || null,
        address_line2: addressLine2 || null,
        city: city || null,
        state: state || null,
        zip_code: zipCode || null,
        phone: phone || null,
        website: website || null,
        accounting_method: accountingMethod
      });
      toast.success('Business profile saved');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleDownloadTemplate(withSampleData) {
    setDownloading(true);
    try {
      const res = await getExpenseReportTemplate(withSampleData, new Date().getFullYear());
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = withSampleData ? 'expense_report_sample.pdf' : 'expense_report_template.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setDownloading(false);
    }
  }

  // Format EIN as XX-XXXXXXX
  function formatEIN(value) {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    if (digits.length > 2) {
      return digits.slice(0, 2) + '-' + digits.slice(2);
    }
    return digits;
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          <div className="skeleton h-40 rounded-lg" />
          <div className="skeleton h-80 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Profile & Settings</h1>
        <p className="text-muted-foreground">Manage your personal and business information</p>
      </div>

      <Tabs defaultValue="personal" className="space-y-6">
        <TabsList className="bg-muted">
          <TabsTrigger value="personal" data-testid="tab-personal">
            <User className="w-4 h-4 mr-2" /> Personal
          </TabsTrigger>
          <TabsTrigger value="business" data-testid="tab-business">
            <Building2 className="w-4 h-4 mr-2" /> Business
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <FileText className="w-4 h-4 mr-2" /> Templates
          </TabsTrigger>
        </TabsList>

        {/* Personal Info Tab */}
        <TabsContent value="personal">
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Your Name</Label>
                  <Input
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="profile-name-input"
                  />
                </div>
                <div>
                  <Label>Industry</Label>
                  <Select value={industry} onValueChange={setIndustry}>
                    <SelectTrigger data-testid="profile-industry-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map(ind => (
                        <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    This helps customize your write-off suggestions
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button className="btn-primary" onClick={handleSavePersonal} disabled={saving} data-testid="save-personal-button">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Save className="w-4 h-4 mr-2" /> Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Info Tab */}
        <TabsContent value="business">
          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Business Name</Label>
                  <Input
                    placeholder="Your Business LLC"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    data-testid="business-name-input"
                  />
                </div>
                <div>
                  <Label>Business Type</Label>
                  <Select value={businessType} onValueChange={setBusinessType}>
                    <SelectTrigger data-testid="business-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {businessTypes.map(bt => (
                        <SelectItem key={bt.value} value={bt.value}>{bt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tax IDs */}
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Tax Identification Numbers
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>EIN (Employer Identification Number)</Label>
                    <Input
                      placeholder="XX-XXXXXXX"
                      value={ein}
                      onChange={(e) => setEin(formatEIN(e.target.value))}
                      maxLength={10}
                      data-testid="business-ein-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Format: XX-XXXXXXX</p>
                  </div>
                  <div>
                    <Label>DUNS Number</Label>
                    <Input
                      placeholder="XXXXXXXXX"
                      value={duns}
                      onChange={(e) => setDuns(e.target.value.replace(/\D/g, '').slice(0, 9))}
                      maxLength={9}
                      data-testid="business-duns-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">9-digit identifier</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="font-semibold mb-4">Business Address</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Address Line 1</Label>
                    <Input
                      placeholder="123 Business Street"
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      data-testid="business-address1-input"
                    />
                  </div>
                  <div>
                    <Label>Address Line 2 (optional)</Label>
                    <Input
                      placeholder="Suite 100"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      data-testid="business-address2-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <Label>City</Label>
                      <Input
                        placeholder="City"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        data-testid="business-city-input"
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Select value={state} onValueChange={setState}>
                        <SelectTrigger data-testid="business-state-select">
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent>
                          {usStates.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>ZIP Code</Label>
                      <Input
                        placeholder="12345"
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        maxLength={5}
                        data-testid="business-zip-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Business Phone</Label>
                  <Input
                    placeholder="(555) 123-4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid="business-phone-input"
                  />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input
                    placeholder="https://yourbusiness.com"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    data-testid="business-website-input"
                  />
                </div>
              </div>

              {/* Accounting */}
              <div>
                <Label>Accounting Method</Label>
                <Select value={accountingMethod} onValueChange={setAccountingMethod}>
                  <SelectTrigger className="w-48" data-testid="business-accounting-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash Basis</SelectItem>
                    <SelectItem value="accrual">Accrual Basis</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Most small businesses use cash basis accounting
                </p>
              </div>

              <div className="flex justify-end">
                <Button className="btn-primary" onClick={handleSaveBusiness} disabled={saving} data-testid="save-business-button">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Save className="w-4 h-4 mr-2" /> Save Business Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>PDF Templates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-foreground">
                Download expense report templates pre-filled with your business information.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="card-hover">
                  <CardContent className="p-6 text-center">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--accent))]" />
                    <h3 className="font-semibold mb-2">Blank Template</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Empty expense report with your business info
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => handleDownloadTemplate(false)}
                      disabled={downloading}
                      data-testid="download-blank-template"
                    >
                      {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      Download Blank
                    </Button>
                  </CardContent>
                </Card>

                <Card className="card-hover">
                  <CardContent className="p-6 text-center">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--warning))]" />
                    <h3 className="font-semibold mb-2">Sample Template</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Pre-filled with sample expense values
                    </p>
                    <Button
                      className="btn-primary"
                      onClick={() => handleDownloadTemplate(true)}
                      disabled={downloading}
                      data-testid="download-sample-template"
                    >
                      {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      Download Sample
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <p className="text-sm">
                    <strong>Tip:</strong> Fill in your business information above first. 
                    The templates will automatically include your business name, EIN, and address.
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
